import { captureException } from "@sentry/core";
import { useCallback, useMemo, useState } from "react";
import { ApiError, useApiClient } from "../api/ApiClient";
import { HttpMethod } from "../shared/models/HttpMethod";
import { CancellationToken } from "../utils";

export function usePaymentClient<T>() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const { send } = useApiClient();

  const doFetch = useCallback(
    async function doFetch(
      {
        path,
        method = "GET",
        body = undefined,
      }: {
        path: string;
        method?: HttpMethod;
        body?: any;
      },
      cancellationToken?: CancellationToken
    ) {
      if (cancellationToken && cancellationToken.isCanceled) return;

      setLoading(true);
      setError(null);

      try {
        const data = await send<T>(method, path, body);

        if (!cancellationToken || !cancellationToken.isCanceled) {
          setData(data);
        }

        return data;
      } catch (error) {
        if (!cancellationToken || !cancellationToken.isCanceled) {
          setError(error as ApiError);
          captureException(error);
        }
      } finally {
        if (!cancellationToken || !cancellationToken.isCanceled) {
          setLoading(false);
        }
      }
    },
    [send]
  );

  const clear = useCallback(() => {
    setError(null);
    setData(null);
    setLoading(false);
  }, []);

  return useMemo(
    () => ({
      loading,
      data,
      error,
      fetch: doFetch,
      clear,
    }),
    [loading, data, error, doFetch, clear]
  );
}
